import React, { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Images, Plus, Pencil, Trash2, Star, ChevronLeft, ChevronRight, X,
  Upload, Loader2, MapPin, CalendarDays, ImageOff, Layers,
} from "lucide-react";
import { galleryApi, errMsg } from "@/lib/api";
import { compressImage } from "@/lib/imageCompress";
import { EmptyState } from "@/components/shared";
import { StaggerGroup, StaggerItem, LazyImage, SkeletonGrid } from "@/components/motion";
import { hoverLift } from "@/lib/motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const STATUS_LABEL = { draft: "Brouillon", published: "Publié", archived: "Archivé" };
const STATUS_CLS = {
  draft: "bg-slate-100 text-slate-700 dark:bg-slate-500/15 dark:text-slate-300",
  published: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  archived: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-200",
};
const emptyForm = { title: "", description: "", location: "", event_date: "", status: "published" };

/* ---------------- Fullscreen viewer (lightbox) ---------------- */
function Lightbox({ photos, index, onClose, onIndex }) {
  const touchX = useRef(null);
  const go = useCallback((dir) => {
    onIndex((i) => (i + dir + photos.length) % photos.length);
  }, [photos.length, onIndex]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, onClose]);

  const p = photos[index];
  if (!p) return null;
  return (
    <div className="fixed inset-0 z-[200] flex flex-col bg-black/95 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label="Visionneuse photo" data-testid="galerie-lightbox"
         onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
         onTouchEnd={(e) => { if (touchX.current == null) return; const dx = e.changedTouches[0].clientX - touchX.current; if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1); touchX.current = null; }}>
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <span className="text-sm tabular-nums" data-testid="galerie-compteur">{index + 1} / {photos.length}</span>
        <button onClick={onClose} aria-label="Fermer" data-testid="galerie-fermer" className="rounded-full p-2 transition-colors hover:bg-white/10"><X className="h-6 w-6" /></button>
      </div>
      <div className="relative flex flex-1 items-center justify-center overflow-hidden px-2">
        <button onClick={() => go(-1)} aria-label="Précédent" data-testid="galerie-prev" className="absolute left-2 z-10 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"><ChevronLeft className="h-6 w-6" /></button>
        <motion.img key={p.id} src={p.image} alt={p.caption || "Photo"} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }} className="max-h-[80vh] max-w-full rounded-lg object-contain" />
        <button onClick={() => go(1)} aria-label="Suivant" data-testid="galerie-next" className="absolute right-2 z-10 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"><ChevronRight className="h-6 w-6" /></button>
      </div>
      {p.caption ? <div className="px-4 py-3 text-center text-sm text-white/80">{p.caption}</div> : <div className="py-3" />}
    </div>
  );
}

/* ---------------- Album detail dialog ---------------- */
function AlbumDialog({ album, isAdmin, onClose, onChanged }) {
  const fileRef = useRef(null);
  const [data, setData] = useState(album);
  const [uploading, setUploading] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(null);

  const reload = async () => { try { const r = await galleryApi.album(album.id); setData(r.data); } catch (e) { toast.error(errMsg(e)); } };
  useEffect(() => { setData(album); }, [album]);

  const photos = data?.photos || [];

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const images = [];
      for (const f of files) images.push(await compressImage(f));
      await galleryApi.addPhotos(album.id, images, "");
      toast.success(`${images.length} photo(s) ajoutée(s)`);
      await reload(); onChanged && onChanged();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const setCover = async (photo) => { try { await galleryApi.updateAlbum(album.id, { cover_image: photo.image }); toast.success("Couverture définie"); onChanged && onChanged(); } catch (e) { toast.error(errMsg(e)); } };
  const toggleFeatured = async (photo) => { try { await galleryApi.updatePhoto(photo.id, { featured: !photo.featured }); await reload(); } catch (e) { toast.error(errMsg(e)); } };
  const removePhoto = async (photo) => { try { await galleryApi.deletePhoto(photo.id); toast.success("Photo supprimée"); await reload(); onChanged && onChanged(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">{data?.title}
            {isAdmin && data?.status !== "published" && <Badge className={STATUS_CLS[data?.status]}>{STATUS_LABEL[data?.status]}</Badge>}
          </DialogTitle>
          <DialogDescription className="sr-only">Détails de l'album et visionneuse des photos</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          {data?.event_date && <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{data.event_date}</span>}
          {data?.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{data.location}</span>}
          <span className="inline-flex items-center gap-1"><Images className="h-3.5 w-3.5" />{photos.length} photo(s)</span>
        </div>
        {data?.description && <p className="text-sm text-muted-foreground">{data.description}</p>}

        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-secondary px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-600" data-testid="galerie-ajouter-photos">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Ajouter des photos
              <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onFiles} data-testid="galerie-photos-input" />
            </label>
          </div>
        )}

        <div className="max-h-[55vh] overflow-y-auto">
          {photos.length === 0 ? <EmptyState title="Aucune photo" description="Cet album ne contient pas encore de photo." icon={ImageOff} /> : (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
              {photos.map((p, i) => (
                <div key={p.id} className="group relative aspect-square overflow-hidden rounded-lg">
                  <button onClick={() => setViewerIndex(i)} className="h-full w-full" aria-label={`Ouvrir la photo ${i + 1}`} data-testid="galerie-vignette">
                    <LazyImage src={p.image} alt={p.caption || `Photo ${i + 1}`} className="h-full w-full" imgClassName="object-cover transition-transform duration-200 group-hover:scale-105" />
                  </button>
                  {p.featured && <span className="absolute left-1 top-1 rounded-full bg-trophy-gold/90 p-1 text-white"><Star className="h-3 w-3 fill-white" /></span>}
                  {isAdmin && (
                    <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1 bg-black/50 p-1 opacity-0 transition-opacity group-hover:opacity-100">
                      <button title="Photo vedette" onClick={() => toggleFeatured(p)} className="rounded p-1 text-white hover:bg-white/20" data-testid="galerie-vedette"><Star className={`h-3.5 w-3.5 ${p.featured ? "fill-trophy-gold text-trophy-gold" : ""}`} /></button>
                      <button title="Définir comme couverture" onClick={() => setCover(p)} className="rounded p-1 text-white hover:bg-white/20" data-testid="galerie-couverture"><Layers className="h-3.5 w-3.5" /></button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild><button title="Supprimer" className="rounded p-1 text-white hover:bg-white/20" data-testid="galerie-photo-supprimer"><Trash2 className="h-3.5 w-3.5" /></button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Supprimer cette photo ?</AlertDialogTitle><AlertDialogDescription>Cette action est définitive.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={() => removePhoto(p)} className="bg-rose-600 hover:bg-rose-700">Supprimer</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
      {viewerIndex !== null && <Lightbox photos={photos} index={viewerIndex} onClose={() => setViewerIndex(null)} onIndex={setViewerIndex} />}
    </Dialog>
  );
}

/* ---------------- Main gallery ---------------- */
export function SportGallery({ sportId, isAdmin }) {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  const load = async () => { setLoading(true); try { const r = await galleryApi.albums(sportId); setAlbums(r.data); } finally { setLoading(false); } };
  useEffect(() => { load(); }, [sportId]);

  const openAlbum = async (id) => { try { const r = await galleryApi.album(id); setActive(r.data); } catch (e) { toast.error(errMsg(e)); } };
  const openNew = () => { setEditing(null); setForm(emptyForm); setFormOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ title: a.title || "", description: a.description || "", location: a.location || "", event_date: a.event_date || "", status: a.status || "published" }); setFormOpen(true); };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Titre requis"); return; }
    setBusy(true);
    try {
      if (editing) { await galleryApi.updateAlbum(editing.id, form); toast.success("Album mis à jour"); }
      else { await galleryApi.createAlbum({ ...form, sport_id: sportId }); toast.success("Album créé"); }
      setFormOpen(false); await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const removeAlbum = async (a) => { try { await galleryApi.deleteAlbum(a.id); toast.success("Album supprimé"); await load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-2 font-heading font-semibold"><Images className="h-4 w-4 text-secondary" />Galerie photo</h3>
        {isAdmin && <Button size="sm" onClick={openNew} data-testid="galerie-nouvel-album"><Plus className="mr-1.5 h-4 w-4" />Nouvel album</Button>}
      </div>

      {loading ? <SkeletonGrid count={4} /> : albums.length === 0 ? (
        <EmptyState title="Aucun album pour l'instant" description={isAdmin ? "Créez un premier album pour partager les photos des entraînements et évènements." : "Les albums photo de ce sport apparaîtront ici."} icon={Images}
          action={isAdmin ? <Button size="sm" variant="outline" onClick={openNew}><Plus className="mr-1.5 h-4 w-4" />Créer un album</Button> : null} />
      ) : (
        <StaggerGroup className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {albums.map((a) => (
            <StaggerItem key={a.id}>
              <motion.div {...hoverLift}>
                <Card className="overflow-hidden" data-testid="galerie-album-carte">
                  <button onClick={() => openAlbum(a.id)} className="block w-full text-left">
                    <div className="relative aspect-video bg-muted">
                      {a.cover_image ? <LazyImage src={a.cover_image} alt={a.title} className="h-full w-full" imgClassName="object-cover" /> : <div className="flex h-full items-center justify-center text-muted-foreground"><ImageOff className="h-8 w-8" /></div>}
                      <span className="absolute bottom-2 right-2 rounded-full bg-black/60 px-2 py-0.5 text-[11px] text-white">{a.photo_count} photo(s)</span>
                      {isAdmin && a.status !== "published" && <span className={`absolute left-2 top-2 rounded-full px-2 py-0.5 text-[11px] ${STATUS_CLS[a.status]}`}>{STATUS_LABEL[a.status]}</span>}
                    </div>
                  </button>
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between gap-2">
                      <button onClick={() => openAlbum(a.id)} className="min-w-0 text-left">
                        <div className="truncate font-semibold">{a.title}</div>
                        <div className="mt-0.5 flex flex-wrap gap-x-3 text-[11px] text-muted-foreground">
                          {a.event_date && <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" />{a.event_date}</span>}
                          {a.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{a.location}</span>}
                        </div>
                      </button>
                      {isAdmin && (
                        <div className="flex shrink-0 gap-1">
                          <button onClick={() => openEdit(a)} className="rounded p-1.5 text-muted-foreground hover:bg-muted" title="Modifier" data-testid="galerie-album-modifier"><Pencil className="h-3.5 w-3.5" /></button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild><button className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-rose-600" title="Supprimer" data-testid="galerie-album-supprimer"><Trash2 className="h-3.5 w-3.5" /></button></AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader><AlertDialogTitle>Supprimer l'album « {a.title} » ?</AlertDialogTitle><AlertDialogDescription>Toutes les photos de cet album seront définitivement supprimées.</AlertDialogDescription></AlertDialogHeader>
                              <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={() => removeAlbum(a)} className="bg-rose-600 hover:bg-rose-700">Supprimer</AlertDialogAction></AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </StaggerItem>
          ))}
        </StaggerGroup>
      )}

      {active && <AlbumDialog album={active} isAdmin={isAdmin} onClose={() => setActive(null)} onChanged={load} />}

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Modifier l'album" : "Nouvel album"}</DialogTitle><DialogDescription className="sr-only">Formulaire de création ou modification d'un album photo</DialogDescription></DialogHeader>
          <div className="grid gap-3">
            <div className="space-y-1.5"><Label>Titre *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="galerie-album-titre" /></div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Date de l'évènement</Label><Input type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} data-testid="galerie-album-date" /></div>
              <div className="space-y-1.5"><Label>Lieu</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="galerie-album-lieu" /></div>
            </div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="galerie-album-description" /></div>
            <div className="space-y-1.5"><Label>Statut</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="galerie-album-statut"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="published">Publié</SelectItem><SelectItem value="draft">Brouillon</SelectItem><SelectItem value="archived">Archivé</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={save} disabled={busy} data-testid="galerie-album-enregistrer">{busy ? "…" : "Enregistrer"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
